import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@data-delivery/d3s';
import type { PermissionApi } from '@data-delivery/permission-ui-api';
import { useMemo, useState } from 'react';

export interface AssignmentUsersCsvDropzoneProps {
  users?: PermissionApi.UserEntity[];
  assignmentId?: string;
  selected?: Record<string, boolean>;
  isEditMode?: boolean;
  className?: string;
  onBulkSelectUsers?: (userIds: string[]) => void;
  onBulkDeselectUsers?: (userIds: string[]) => void;
}

async function readCsvTokens(file: File): Promise<string[]> {
  const text = await file.text();

  return text
    .split(/[\r\n,]+/)
    .map(l => l.trim())
    .filter(Boolean);
}

function normalize(val: string | undefined): string {
  return (val || '').trim().toLowerCase();
}

export function matchTokensToUserIds(
  tokens: string[],
  usersPool: PermissionApi.UserEntity[],
): string[] {
  if (!tokens.length || !usersPool.length) {
    return [];
  }

  const byLoginId = new Map<string, string[]>();
  const byName = new Map<string, string[]>();
  const byFullName = new Map<string, string[]>();

  for (const u of usersPool) {
    const loginKey = normalize(u.loginId);
    const firstKey = normalize(u.firstName);
    const lastKey = normalize(u.lastName);
    const fullNameKey = normalize(`${u.firstName || ''} ${u.lastName || ''}`.trim());

    if (loginKey) {
      const arr = byLoginId.get(loginKey) ?? [];
      arr.push(u.id);
      byLoginId.set(loginKey, arr);
    }

    if (firstKey) {
      const arr = byName.get(firstKey) ?? [];
      arr.push(u.id);
      byName.set(firstKey, arr);
    }

    if (lastKey) {
      const arr = byName.get(lastKey) ?? [];
      arr.push(u.id);
      byName.set(lastKey, arr);
    }

    if (fullNameKey) {
      const arr = byFullName.get(fullNameKey) ?? [];
      arr.push(u.id);
      byFullName.set(fullNameKey, arr);
    }
  }

  const result = new Set<string>();
  for (const t of tokens) {
    const key = normalize(t);
    const loginHits = byLoginId.get(key) ?? [];
    const nameHits = byName.get(key) ?? [];
    const fullNameHits = byFullName.get(key) ?? [];

    for (const id of [...loginHits, ...nameHits, ...fullNameHits]) {
      result.add(id);
    }
  }

  return Array.from(result);
}

export const AssignmentUsersCsvDropzone: React.FC<AssignmentUsersCsvDropzoneProps> = ({
  users = [],
  assignmentId,
  selected = {},
  isEditMode,
  className,
  onBulkSelectUsers,
  onBulkDeselectUsers,
}) => {
  const [mode, setMode] = useState<'select' | 'deselect'>('select');
  const [open, setOpen] = useState(false);
  const modeOptions = useMemo(
    () => [
      { value: 'select', label: 'Select users' },
      { value: 'deselect', label: 'Deselect users' },
    ],
    [],
  );

  async function handleBulkSelect(file: File) {
    const tokens = await readCsvTokens(file);
    const matched = matchTokensToUserIds(tokens, users);

    const notSelected = matched.filter(id => !selected[id]);
    onBulkSelectUsers?.(notSelected);
  }

  async function handleBulkDeselect(file: File) {
    const tokens = await readCsvTokens(file);
    const matched = matchTokensToUserIds(tokens, users);

    const currentlySelected = matched.filter(id => selected[id]);
    onBulkDeselectUsers?.(currentlySelected);
  }

  return (
    <div className={className}>
      <div className='flex items-center gap-2 mb-2'>
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <button type='button' className='rounded-md border px-3 py-2 text-xs hover:bg-muted'>
              {open ? 'Hide bulk CSV' : 'Show bulk CSV'}
            </button>
          </CollapsibleTrigger>
        </Collapsible>
      </div>

      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleContent>
          <div className='flex flex-col gap-3 w-full'>
            <div className='text-xs text-muted-foreground'>
              Use this area to bulk manage user selection via a CSV file. Choose whether to{' '}
              <span className='font-medium'>select</span> users to assign or{' '}
              <span className='font-medium'>deselect</span> users already selected, then upload a
              CSV or drag-and-drop it here.
              <br />
              <br />
              CSV format: one token per line or comma-separated. Tokens can be{' '}
              <span className='font-medium'>Login ID</span>,{' '}
              <span className='font-medium'>First Name</span>,{' '}
              <span className='font-medium'>Last Name</span>, or{' '}
              <span className='font-medium'>Full Name</span>.
            </div>
            <div className='flex items-center gap-2'>
              <Select
                value={mode}
                onValueChange={val => {
                  setMode((val as 'select' | 'deselect') ?? 'select');
                }}
              >
                <SelectTrigger className='w-[220px]'>
                  <SelectValue placeholder='Choose action' />
                </SelectTrigger>
                <SelectContent>
                  {modeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='flex items-stretch gap-2 w-full'>
              <Dropzone
                accept={{ 'text/csv': ['.csv'], 'text/plain': ['.txt'] }}
                maxFiles={1}
                disabled={
                  mode === 'deselect' && (!isEditMode || Object.keys(selected).length === 0)
                }
                onDrop={async accepted => {
                  const f = accepted?.[0];
                  if (!f) {
                    return;
                  }

                  if (mode === 'deselect') {
                    await handleBulkDeselect(f);
                  } else {
                    await handleBulkSelect(f);
                  }
                }}
                className='flex-1'
              >
                <DropzoneEmptyState />
                <DropzoneContent />
              </Dropzone>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default AssignmentUsersCsvDropzone;
